import { useRef, useState } from 'react';
import { Button } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { AliasPills } from 'components/alias-pills';
import ErrorToast from 'components/error-toast';
import type { AssetAlias } from 'types/api-responses';
import { AddAliasModal } from './add-alias-modal';
import { useAssetAliases } from './hooks';
import styles from './alias-manager.module.scss';

interface AliasManagerProps {
  aliases: AssetAlias[];
  /** The record's alias endpoint, e.g. `/crm/tracts/a00f4…/aliases` or `/development_areas/1001/aliases`. */
  aliasesPath: string;
  /** Called with the record's full alias list after an add or a remove. */
  onChange: (aliases: AssetAlias[]) => void;
  /** What the aliases are on, in the modal's helper text: "tract" or "development area". */
  recordLabel: string;
  /** The record's number (T-… or U-…), the Add alias dialog's label. */
  recordNumber: string;
}

/**
 * A record's aliases as dismissible pills with an Add Alias button that opens the Add alias dialog.
 * Meant for a page header, beside the record's name.
 * @param {AliasManagerProps} props - The aliases, where to send changes, and what the record is.
 * @return {JSX.Element} The pills and the button.
 */
export function AliasManager({
  aliases,
  aliasesPath,
  onChange,
  recordLabel,
  recordNumber,
}: AliasManagerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const { add, isAdding, remove, removingIds, removeError } = useAssetAliases({
    aliasesPath,
    onChange,
  });

  return (
    <div className={styles.manager}>
      <AliasPills
        aliases={aliases}
        removingIds={removingIds}
        onRemove={remove}
        onEmptied={() => addButtonRef.current?.focus()}
      />
      <Button
        ref={addButtonRef}
        kind="tertiary"
        size="sm"
        renderIcon={Add}
        onClick={() => setIsModalOpen(true)}
      >
        Add Alias
      </Button>
      <AddAliasModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={add}
        isAdding={isAdding}
        existingAliases={aliases}
        recordLabel={recordLabel}
        recordNumber={recordNumber}
      />
      {removeError && <ErrorToast key={removeError} message={removeError} />}
    </div>
  );
}
